import { useCallback, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { api } from '../../services/api'
import { useAccount } from '../../hooks/useAccount'
import { useSubscription } from '../../hooks/useSubscription'
import { useAuth } from '../../hooks/useAuth'
import {
  trackStartTrial,
  collectBrowserContext,
  hashEmail,
  hashExternalId,
} from '../../utils/analytics'
import AccountCard from './AccountCard'
import DisconnectedAccountCard from './DisconnectedAccountCard'
import DisconnectAccountModal from './DisconnectAccountModal'
import DeleteBlockedModal from './DeleteBlockedModal'
import DeleteAccountModal from './DeleteAccountModal'
import RecoveryModal from './RecoveryModal'
import BrandSettingsOnboardingModal from './BrandSettingsOnboardingModal'
import { Accordion } from '../shared/Accordion'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

const onboardingModalStorageKey = 'pendingOnboardingModal'

const readPendingOnboardingModal = () => {
  try {
    const data = sessionStorage.getItem(onboardingModalStorageKey)
    return data ? JSON.parse(data) : null
  } catch {
    return null
  }
}

const writePendingOnboardingModal = (data) => {
  try {
    sessionStorage.setItem(onboardingModalStorageKey, JSON.stringify(data))
    window.dispatchEvent(new Event('pendingOnboardingModalUpdated'))
  } catch {
    // Ignore storage errors (e.g., storage unavailable).
  }
}

const clearPendingOnboardingModal = () => {
  try {
    sessionStorage.removeItem(onboardingModalStorageKey)
    window.dispatchEvent(new Event('pendingOnboardingModalUpdated'))
  } catch {
    // Ignore storage errors (e.g., storage unavailable).
  }
}

// Toast component
function Toast({ message, type = 'info', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000)
    return () => clearTimeout(timer)
  }, [onClose])

  const typeStyles = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
    warning: 'bg-yellow-500'
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2">
      <div className={`${typeStyles[type]} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2`}>
        <span>{message}</span>
        <button
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

// Empty state component
function EmptyState({ onConnect }) {
  const { t } = useTranslation('settings')

  return (
    <div className="flex flex-col items-center justify-center py-16 max-w-4xl mx-auto">
      <div className="text-6xl mb-4">📷</div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {t('accounts.page.empty.title')}
      </h3>
      <p className="text-gray-600 text-center max-w-md mb-6">
        {t('accounts.page.empty.message')}
      </p>
      <div className="flex space-x-3">
        <button
          onClick={() => onConnect('instagram')}
          className="px-6 py-3 text-white bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all"
        >
          {t('accounts.page.empty.buttonInstagram')}
        </button>
        <button
          onClick={() => onConnect('facebook')}
          className="px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all"
          title={t('accounts.page.empty.buttonFacebookHint')}
        >
          {t('accounts.page.empty.buttonFacebook')}
        </button>
      </div>

      {/* FAQ Section */}
      <div className="w-full mt-12">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          {t('accounts.page.empty.faq.title')}
        </h4>
        <div className="space-y-3">
          <Accordion
            title={t('accounts.page.empty.faq.questions.connectionMethods.question')}
            icon="🔗"
            variant="neutral"
          >
            <p className="text-sm text-gray-700 mt-2">
              {t('accounts.page.empty.faq.questions.connectionMethods.answer')}
            </p>
          </Accordion>

          <Accordion
            title={t('accounts.page.empty.faq.questions.accountType.question')}
            icon="💼"
            variant="neutral"
          >
            <p className="text-sm text-gray-700 mt-2">
              {t('accounts.page.empty.faq.questions.accountType.answer')}
            </p>
          </Accordion>

          <Accordion
            title={t('accounts.page.empty.faq.questions.whichMethod.question')}
            icon="🤔"
            variant="neutral"
          >
            <p className="text-sm text-gray-700 mt-2">
              {t('accounts.page.empty.faq.questions.whichMethod.answer')}
            </p>
          </Accordion>

          <Accordion
            title={t('accounts.page.empty.faq.questions.permissions.question')}
            icon="🔐"
            variant="neutral"
          >
            <p className="text-sm text-gray-700 mt-2">
              {t('accounts.page.empty.faq.questions.permissions.answer')}
            </p>
          </Accordion>
        </div>
      </div>
    </div>
  )
}

function AccountsPage() {
  const { t } = useTranslation('settings')
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    accounts,
    selectedAccountId,
    selectAccount,
    refreshAccounts,
    removeAccount,
    isLoadingAccounts
  } = useAccount()

  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)
  const [localAccounts, setLocalAccounts] = useState([])

  // Track if this is user's first account connection (for StartTrial tracking)
  const wasFirstAccountRef = useRef(false)

  // Modal states
  const [disconnectModal, setDisconnectModal] = useState({ isOpen: false, account: null })
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, account: null })
  const [deleteBlockedModal, setDeleteBlockedModal] = useState({ isOpen: false, account: null })
  const [recoveryModal, setRecoveryModal] = useState({ isOpen: false, account: null })
  const [onboardingModal, setOnboardingModal] = useState({ isOpen: false, accountId: null, accountUsername: null })
  const [pendingModalData, setPendingModalData] = useState(() => readPendingOnboardingModal())

  // Separate accounts into active and "needs attention". An account whose
  // Instagram publishing connection has expired (`needs_reconnect: true`, set by
  // the backend after auto-retry of transient publish failures is exhausted) is
  // grouped with hard-disconnected accounts so it renders a DisconnectedAccountCard
  // that exposes the existing reconnect (OAuth re-init) action. Without this, such
  // an account would stay in the "active" list with no way to reconnect, and the
  // global InstagramReconnectModal CTA -> /accounts would lead nowhere.
  const needsReconnect = acc => acc.is_disconnected || acc.status === 'disconnected' || acc.needs_reconnect
  const activeAccounts = localAccounts.filter(acc => !needsReconnect(acc))
  const disconnectedAccounts = localAccounts.filter(needsReconnect)

  // Sync local accounts with context accounts
  useEffect(() => {
    setLocalAccounts(accounts)
  }, [accounts])

  useEffect(() => {
    const handlePendingModalUpdate = () => {
      setPendingModalData(readPendingOnboardingModal())
    }

    handlePendingModalUpdate()
    window.addEventListener('pendingOnboardingModalUpdated', handlePendingModalUpdate)
    window.addEventListener('storage', handlePendingModalUpdate)
    return () => {
      window.removeEventListener('pendingOnboardingModalUpdated', handlePendingModalUpdate)
      window.removeEventListener('storage', handlePendingModalUpdate)
    }
  }, [])

  // Error codes from OAuth redirect mapped to i18n keys
  const OAUTH_ERROR_MESSAGES = {
    'invalid_session': 'accounts.oauth.errors.invalidSession',
    'session_expired': 'accounts.oauth.errors.sessionExpired',
    'auth_failed': 'accounts.oauth.errors.authFailed',
    'access_denied': 'accounts.oauth.errors.accessDenied',
    'account_already_linked': 'accounts.oauth.errors.accountAlreadyLinked',
    'unknown_error': 'accounts.account.messages.connectError',
  }

  // Handle OAuth redirect callback (query parameters)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const oauthStatus = params.get('oauth')
    const accountId = params.get('account_id')
    const username = params.get('username')
    const errorCode = params.get('code')

    // Skip if no oauth parameter present
    if (!oauthStatus) return

    // Clean URL immediately (remove query params)
    const cleanUrl = window.location.pathname
    window.history.replaceState({}, '', cleanUrl)

    if (oauthStatus === 'success') {
      // Show success toast
      setToast({
        message: username
          ? t('accounts.account.messages.connected', { username: `@${username}` })
          : t('accounts.account.messages.connectSuccess'),
        type: 'success'
      })

      // Check if this was first account (for StartTrial pixel tracking)
      const wasFirstAccount = sessionStorage.getItem('oauth_first_account') === 'true'
      sessionStorage.removeItem('oauth_first_account')

      if (wasFirstAccount && user?.id) {
        // Track StartTrial Pixel event
        const eventId = `${user.id}_StartTrial_first_account`
        Promise.all([
          hashEmail(user.email),
          hashExternalId(user.id)
        ]).then(([hashedEmail, hashedExternalId]) => {
          trackStartTrial({ eventId, hashedEmail, hashedExternalId })
        })
      }

      // Refresh accounts from backend
      refreshAccounts().then(async (refreshedAccounts) => {
        if (refreshedAccounts) {
          setLocalAccounts(refreshedAccounts)

          // Find the newly connected account by ID
          if (accountId) {
            const newAccount = refreshedAccounts.find(acc => acc.id === accountId)

            if (newAccount) {
              // Select the new account
              selectAccount(newAccount.id)

              // Check brand settings and show onboarding modal if needed
              const hasBrandSettings = await checkBrandSettings(newAccount.id)
              if (!hasBrandSettings) {
                const modalData = {
                  accountId: newAccount.id,
                  accountUsername: newAccount.instagram_username || username
                }
                setOnboardingModal({ isOpen: true, ...modalData })
                writePendingOnboardingModal(modalData)
              }
            }
          }
        }
      }).catch(err => {
        console.error('[OAuth] Failed to refresh accounts:', err)
      })

    } else if (oauthStatus === 'error') {
      // Show error toast with appropriate message
      const errorKey = OAUTH_ERROR_MESSAGES[errorCode] || OAUTH_ERROR_MESSAGES['unknown_error']
      setToast({
        message: t(errorKey),
        type: 'error'
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps - only run once on mount

  // Compute effective modal state - either from React state OR from sessionStorage
  const effectiveOnboardingModal = onboardingModal.isOpen
    ? onboardingModal
    : pendingModalData
      ? { isOpen: true, accountId: pendingModalData.accountId, accountUsername: pendingModalData.accountUsername }
      : onboardingModal


  // Check if account has brand settings (tone-of-voice examples)
  const checkBrandSettings = useCallback(async (accountId) => {
    try {
      const response = await api.getBrandSettings(accountId)
      const examples = response?.tone_of_voice_examples || []

      // Check if has any non-empty examples
      return examples.length > 0 && examples.some(ex => ex?.trim())
    } catch (error) {
      console.error('Failed to check brand settings:', error)
      // Show modal on error (safer default - better to show than not show)
      return false
    }
  }, [])

  // OAuth connection handler - redirect-based flow (no popup)
  const handleConnectAccount = useCallback(async (provider = 'instagram') => {
    try {
      setConnecting(true)
      setError(null)

      // Track if this is user's first account (for StartTrial) - store in sessionStorage for after redirect
      const activeAccountCount = localAccounts.filter(acc => !acc.is_disconnected && acc.status !== 'disconnected').length
      const isFirstAccount = activeAccountCount === 0
      if (isFirstAccount) {
        sessionStorage.setItem('oauth_first_account', 'true')
      }

      // Prepare CAPI context for StartTrial tracking (only for first account)
      let oauthOptions = {}
      if (isFirstAccount && user?.id) {
        const eventId = `${user.id}_StartTrial_first_account`
        const browserContext = collectBrowserContext()
        oauthOptions = {
          capi_event_id: eventId,
          capi_context: browserContext,
        }
      }

      // Get OAuth URL from backend based on provider
      const response = provider === 'facebook'
        ? await api.initiateFacebookOAuth(oauthOptions)
        : await api.initiateInstagramOAuth(oauthOptions)
      const oauthUrl = response.oauth_url || response.url

      if (!oauthUrl) {
        throw new Error('No OAuth URL received from server')
      }

      // Redirect to OAuth URL (instead of popup)
      // After OAuth, backend will redirect back to /accounts with query params
      window.location.href = oauthUrl

    } catch (error) {
      setConnecting(false)
      setError(error.message)
      setToast({
        message: error.message,
        type: 'error'
      })
    }
  }, [refreshAccounts, selectAccount, t, checkBrandSettings, localAccounts, user])

  // Token refresh handler
  const handleRefreshToken = useCallback(async (accountId) => {
    try {
      const response = await api.refreshToken(accountId)

      // Update account in local state optimistically
      setLocalAccounts(prev => prev.map(acc =>
        acc.id === accountId
          ? { ...acc, token_expires_at: response.expires_at || response.token_expires_at }
          : acc
      ))

      // Refresh accounts from server
      await refreshAccounts()

      // Show success toast
      setToast({
        message: t('accounts.account.messages.tokenRefreshed', {
          date: format(new Date(response.expires_at || response.token_expires_at), 'PP')
        }),
        type: 'success'
      })
    } catch (error) {
      const isSessionInvalidated = error.response?.data?.reason === 'reconnect_required'
      setToast({
        message: isSessionInvalidated
          ? t('accounts.account.messages.tokenSessionInvalidated')
          : t('accounts.account.messages.tokenRefreshError', { error: error.message }),
        type: 'error'
      })
    }
  }, [refreshAccounts, t])

  // Disconnect account handler
  const handleDisconnect = useCallback(async (account) => {
    // Get scheduled story count for this account
    try {
      const stories = await api.getAccountStories(account.id, { status: 'scheduled' })
      const scheduledCount = stories?.stories?.length || 0
      setDisconnectModal({ isOpen: true, account, scheduledCount })
    } catch (error) {
      // Show modal anyway, just without the count
      setDisconnectModal({ isOpen: true, account, scheduledCount: 0 })
    }
  }, [])

  const confirmDisconnect = useCallback(async () => {
    const account = disconnectModal.account
    if (!account) return

    try {
      await api.disconnectAccount(account.id)

      // Update local state to show as disconnected (optimistic)
      setLocalAccounts(prev => prev.map(acc =>
        acc.id === account.id
          ? { ...acc, is_disconnected: true, status: 'disconnected' }
          : acc
      ))

      setToast({
        message: t('accounts.disconnect.success', { username: account.username }),
        type: 'success'
      })

      // Refresh accounts from server and sync local state
      const refreshedAccounts = await refreshAccounts()
      if (refreshedAccounts) {
        setLocalAccounts(refreshedAccounts)
      }
    } catch (error) {
      setToast({
        message: t('accounts.disconnect.error', { error: error.message }),
        type: 'error'
      })
    }
  }, [disconnectModal.account, refreshAccounts, t])

  // Delete account handler
  const handleDelete = useCallback(async (account) => {
    // Check if account has active subscription
    const { plan } = await api.getAccountSubscription(account.id)

    if (plan && plan !== 'free') {
      // Show blocked modal if subscription is active
      setDeleteBlockedModal({ isOpen: true, account, plan })
    } else {
      // Show delete confirmation modal
      setDeleteModal({ isOpen: true, account })
    }
  }, [])

  const confirmDelete = useCallback(async (accountId, confirmation) => {
    try {
      const result = await api.scheduleAccountDeletion(accountId, confirmation)

      if (result?.recovery_url) {
        // Account scheduled for deletion
        setToast({
          message: t('accounts.delete.scheduled'),
          type: 'success'
        })

        // Refresh accounts and update local state
        const refreshedAccounts = await refreshAccounts()
        if (refreshedAccounts) {
          setLocalAccounts(refreshedAccounts)
        }

        return result
      }
    } catch (error) {
      setToast({
        message: t('accounts.delete.error', { error: error.message }),
        type: 'error'
      })
      throw error
    }
  }, [refreshAccounts, t])

  // Recovery handler
  const handleRecover = useCallback(async (accountId) => {
    try {
      await api.recoverAccount(accountId)

      // Refresh accounts and update local state
      const refreshedAccounts = await refreshAccounts()
      if (refreshedAccounts) {
        setLocalAccounts(refreshedAccounts)
      }

      setToast({
        message: t('accounts.recovery.success'),
        type: 'success'
      })
    } catch (error) {
      setToast({
        message: t('accounts.recovery.error', { error: error.message }),
        type: 'error'
      })
      throw error
    }
  }, [refreshAccounts, t])

  // Reconnect handler
  const handleReconnect = useCallback(async (accountId) => {
    // For now, just initiate OAuth again
    // In a real implementation, this might have special handling
    await handleConnectAccount()
  }, [handleConnectAccount])

  // Open Stripe portal handler
  const handleOpenStripePortal = useCallback(async (accountId) => {
    try {
      const response = await api.createStripePortalSession(accountId)

      if (response?.url) {
        // Open Stripe portal in new tab
        window.open(response.url, '_blank')
      } else {
        throw new Error('No portal URL received')
      }
    } catch (error) {
      setToast({
        message: t('accounts.billing.error', { error: error.message }),
        type: 'error'
      })
    }
  }, [t])

  // Cancel subscription handler (redirect to Stripe portal)
  const handleCancelSubscription = useCallback(async (accountId) => {
    await handleOpenStripePortal(accountId)
  }, [handleOpenStripePortal])

  // Retry handler for errors
  const handleRetry = useCallback(() => {
    setError(null)
    refreshAccounts()
  }, [refreshAccounts])

  // Brand settings handler
  const handleBrandSettings = useCallback((accountId) => {
    navigate(`/accounts/${accountId}/brand-settings`)
  }, [navigate])

  // Subscription management handler
  const handleManageSubscription = useCallback((accountId) => {
    // Select the account first (so useSelectedAccountId in SubscriptionPage works)
    selectAccount(accountId)
    // Navigate to subscription page
    navigate('/subscription')
  }, [navigate, selectAccount])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('accounts.page.title')}</h1>
          <p className="mt-2 text-gray-600">{t('accounts.page.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Instagram Direct OAuth (Primary) */}
          <button
            onClick={() => handleConnectAccount('instagram')}
            disabled={connecting}
            className="w-full sm:w-auto px-5 py-2.5 text-white bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {connecting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('accounts.page.button.connecting')}</span>
              </>
            ) : (
              <span>{t('accounts.page.empty.buttonInstagram')}</span>
            )}
          </button>

          {/* Facebook OAuth (Legacy) */}
          <button
            onClick={() => handleConnectAccount('facebook')}
            disabled={connecting}
            className="w-full sm:w-auto px-5 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            title={t('accounts.page.empty.buttonFacebookHint')}
          >
            {connecting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('accounts.page.button.connecting')}</span>
              </>
            ) : (
              <span>{t('accounts.page.empty.buttonFacebook')}</span>
            )}
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoadingAccounts ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? (
        /* Error state */
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <span className="text-2xl mr-3">⚠️</span>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900">{t('accounts.page.error.title')}</h3>
              <p className="text-red-700 mt-1">{error}</p>
              <button
                onClick={handleRetry}
                className="mt-4 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                {t('accounts.page.error.button')}
              </button>
            </div>
          </div>
        </div>
      ) : localAccounts.length === 0 ? (
        /* Empty state */
        <EmptyState onConnect={handleConnectAccount} />
      ) : (
        /* Accounts sections */
        <div className="space-y-8">
          {/* Active Accounts Section */}
          {activeAccounts.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <h2 className="text-lg font-semibold text-gray-900">{t('accounts.sections.active')}</h2>
                <span className="text-sm text-gray-500">({activeAccounts.length})</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {activeAccounts.map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account}
                    isSelected={selectedAccountId === account.id}
                    onSelect={selectAccount}
                    onRefreshToken={handleRefreshToken}
                    onDisconnect={handleDisconnect}
                    onDelete={handleDelete}
                    onBrandSettings={handleBrandSettings}
                    onManageSubscription={handleManageSubscription}
                    onOpenStripePortal={handleOpenStripePortal}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Disconnected Accounts Section */}
          {disconnectedAccounts.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                <h2 className="text-lg font-semibold text-gray-900">{t('accounts.sections.disconnected')}</h2>
                <span className="text-sm text-gray-500">({disconnectedAccounts.length})</span>
              </div>

              {/* Info banner */}
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">{t('accounts.disconnected.info.title')}</p>
                    <p>{t('accounts.disconnected.info.message')}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {disconnectedAccounts.map((account) => (
                  <DisconnectedAccountCard
                    key={account.id}
                    account={account}
                    onReconnect={handleReconnect}
                    onDelete={handleDelete}
                    onOpenStripePortal={handleOpenStripePortal}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Modals */}
      <DisconnectAccountModal
        isOpen={disconnectModal.isOpen}
        onClose={() => setDisconnectModal({ isOpen: false, account: null })}
        onConfirm={confirmDisconnect}
        account={disconnectModal.account}
        scheduledStoryCount={disconnectModal.scheduledCount || 0}
      />

      <DeleteBlockedModal
        isOpen={deleteBlockedModal.isOpen}
        onClose={() => setDeleteBlockedModal({ isOpen: false, account: null })}
        onCancelSubscription={handleCancelSubscription}
        account={deleteBlockedModal.account}
        subscriptionPlan={deleteBlockedModal.plan}
      />

      <DeleteAccountModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, account: null })}
        onConfirm={confirmDelete}
        account={deleteModal.account}
      />

      <RecoveryModal
        isOpen={recoveryModal.isOpen}
        onClose={() => setRecoveryModal({ isOpen: false, account: null })}
        onRecover={handleRecover}
        account={recoveryModal.account}
        deletedAt={recoveryModal.account?.deleted_at}
      />

      {/* Brand Settings Onboarding Modal */}
      <BrandSettingsOnboardingModal
        isOpen={effectiveOnboardingModal.isOpen}
        onClose={() => {
          // Clear both React state and sessionStorage
          clearPendingOnboardingModal()
          setOnboardingModal({ isOpen: false, accountId: null, accountUsername: null })
        }}
        accountId={effectiveOnboardingModal.accountId}
        accountUsername={effectiveOnboardingModal.accountUsername}
      />
    </div>
  )
}

export default AccountsPage
